/** Env from Vite (import.meta.env) or fallbacks for dev. */

/**
 * `import.meta.env` is injected by the bundler and exists nowhere else. Under plain Node —
 * the test runner — `import.meta.env` is `undefined`, so every unguarded `.VITE_X` read is a
 * TypeError on property access, thrown before the function can reach any of its fallbacks.
 *
 * That one detail is why this module was untestable, and with it everything downstream:
 * `api.ts` calls `getApiUrl()` on the first line of every request verb, so the fetch wrapper
 * that every authenticated request passes through could not be imported into a test at all.
 * The 43-mutation sweep measured the consequence — 106 tests executing 4 of 71 modules.
 *
 * Reading through one guarded accessor changes nothing under Vite (`import.meta.env` is a real
 * object there, carrying every VITE_-prefixed var plus MODE/DEV/PROD) and makes the module
 * importable outside it, where the object is simply empty and each getter falls through to the
 * default it already documents.
 *
 * This module is the single owner of that read. `src/main.tsx` and `src/lib/sentry.ts` still
 * touch `import.meta.env` directly and deliberately: main.tsx IS the Vite entry point and
 * sentry.ts is only ever reached from it, so neither can run outside the bundler and neither
 * needs to be importable from a test. Anything that must be — and that is everything the app's
 * request path touches — reads env through here.
 */
export const viteEnv = (): Partial<ImportMetaEnv> => import.meta.env ?? {};

/**
 * API-Basis-URL für fetch / Hono-Client.
 * - VITE_API_URL: explizit (Staging, Split-Deploy, Tests).
 * - Browser Production/Preview: immer **Same-Origin** `/api` — damit Vercel-Web+API
 *   im selben Projekt (takelead.app *und* *.vercel.app) dieselbe Deployment-Instanz trifft.
 *   Hardcoded takelead.app würde Preview-Frontends fälschlich gegen Production-API schicken.
 * - Vite dev: lokaler API-Server (nicht 5173).
 */
export function getApiUrl(): string {
  const url = viteEnv().VITE_API_URL as string | undefined;
  if (url && url.trim()) return url.trim().replace(/\/$/, "");
  if (typeof window !== "undefined") {
    if (viteEnv().DEV) {
      return "http://localhost:3000";
    }
    return `${window.location.origin}/api`;
  }
  if (viteEnv().PROD) return "https://takelead.app/api";
  return "http://localhost:3000";
}

export function getSupabaseUrl(): string {
  return (viteEnv().VITE_SUPABASE_URL as string) ?? "";
}

export function getSupabaseAnonKey(): string {
  return (viteEnv().VITE_SUPABASE_ANON_KEY as string) ?? "";
}

export function hasSupabaseConfig(): boolean {
  const url = getSupabaseUrl();
  const key = getSupabaseAnonKey();
  return Boolean(url && key && !url.includes("placeholder"));
}

/** "staging" = geschützte Phase (Banner anzeigen); nur bei "live" = öffentlich. Default ist staging. Für Go-Live: VITE_LAUNCH_MODE=live setzen. */
export function getLaunchMode(): "staging" | "live" {
  const v = (viteEnv().VITE_LAUNCH_MODE as string)?.toLowerCase();
  return v === "live" ? "live" : "staging";
}

export function isStagingMode(): boolean {
  return getLaunchMode() === "staging";
}
